import { ConnectError } from '@bufbuild/connect';
import { Timestamp } from '@bufbuild/protobuf';
import { endOfDay, startOfDay } from 'date-fns';
import { useSearchParams } from 'react-router';

import api from '@/shared/api/api';
import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppDispatch } from '@/shared/hooks';
import { debug, isConnectNetworkError, parseDateAsLocal } from '@/shared/lib';
import { getAgeGroup } from '@/shared/lib/getAgeGroup';

import { patientModel } from '@/entities/patient';
import { StudyCount, studyCountModel } from '@/entities/studyCount';

import { AllPatientsFilterSettingsType } from '@/pages/Patients/config/types';

export type UsePatientsListStreamArgs = {
  organizationID: string;
};

let abortController: AbortController;

export const usePatientsListStream = ({
  organizationID,
}: UsePatientsListStreamArgs) => {
  // TODO: Resolve issue with previous searchParams date on reopening stream
  const [searchParams] = useSearchParams();

  const doctorIDs = searchParams.get('doctorIDs') ?? '';
  const doctorFiltersIsEmpty = !searchParams.get('doctorIDs');

  const searchValue = searchParams.get('search') ?? '';
  const doctorFilter = doctorFiltersIsEmpty ? [] : doctorIDs.split(',');

  const studyTypesParam = searchParams.get('studyTypes') ?? '';
  const studyTypesFiltersIsEmpty = !studyTypesParam;
  const studyTypesFilter = studyTypesFiltersIsEmpty
    ? []
    : studyTypesParam.split(',');

  const ageGroupParam = searchParams.get('ageGroup')?.split(',');
  const ageGroupFilter = getAgeGroup(ageGroupParam);

  const lastModifiedFromParam = searchParams.get('lastModifiedFrom');
  const lastModifiedToParam = searchParams.get('lastModifiedTo');
  const lastModifiedFromUrl = lastModifiedFromParam
    ? parseDateAsLocal(lastModifiedFromParam)
    : undefined;
  const lastModifiedToUrl = lastModifiedToParam
    ? parseDateAsLocal(lastModifiedToParam)
    : undefined;

  const dispatch = useAppDispatch();

  const openPatientListStream = async (
    filterSettings: AllPatientsFilterSettingsType = {
      searchString: searchValue,
      doctorFilters: doctorFilter,
      id: '',
      studyTypes: studyTypesFilter,
      ageGroup: ageGroupFilter,
      lastModifiedFrom: lastModifiedFromUrl,
      lastModifiedTo: lastModifiedToUrl,
    },
  ) => {
    abortController = new AbortController();

    const {
      searchString,
      id,
      doctorFilters,
      sortBy,
      studyTypes,
      ageGroup,
      lastModifiedFrom: fromDate,
      lastModifiedTo: toDate,
      hasTranscriptor,
    } = filterSettings;

    // Convert studyTypes from string[] to StudyType[] before use
    const convertedStudyTypes = studyTypes?.map(
      (key) => Number(key) as StudyType,
    );

    dispatch(patientModel.actions.setLoading('pending'));

    try {
      const patientListStream = api.patient.patientListStream(
        {
          OrganizationID: organizationID,
          SearchString: searchString,
          Doctors: {
            case: doctorFilters?.length ? 'OnlyTheseDoctors' : 'AllDoctors',
            value: doctorFilters?.length ? { DoctorIDs: doctorFilters } : {},
          },
          Age: ageGroup,
          Studies: {
            case: convertedStudyTypes?.length
              ? 'OnlyStudyTypes'
              : 'AllStudyTypes',
            value: convertedStudyTypes?.length
              ? { StudyTypes: convertedStudyTypes }
              : {},
          },
          Sort: sortBy,
          StartFromPatientID: id,
          Limit: 20,
          UpdatedAtFrom: fromDate
            ? Timestamp.fromDate(startOfDay(fromDate))
            : undefined,
          UpdatedAtTo: toDate
            ? Timestamp.fromDate(endOfDay(toDate))
            : undefined,
          HasTranscriptorEncounters: hasTranscriptor,
        },
        { signal: abortController.signal },
      );

      for await (const { Update } of patientListStream) {
        switch (Update.case) {
          case 'InitialPatientsListItems': {
            let patients: Patient[] = [];
            let studyCounts: StudyCount[] = [];

            Update.value?.Patients.forEach(({ Patient, Studies }) => {
              patients.push(Patient as Patient);

              studyCounts.push({
                patientID: Patient?.ID as string,
                counts: Studies,
              });
            });

            // adding patients to store
            if (id) {
              dispatch(patientModel.actions.addMany(patients as Patient[]));
            } else {
              dispatch(patientModel.actions.setMany(patients));
            }

            dispatch(patientModel.actions.setLoading('succeeded'));

            // adding study counts to store
            dispatch(studyCountModel.actions.setMany(studyCounts));

            // clear accumulated data
            patients = [];
            studyCounts = [];
            break;
          }

          case 'PatientUpdated': {
            if (Update.value.Patient) {
              dispatch(patientModel.actions.setNewestOne(Update.value.Patient));
            }
            const studyCount = {
              patientID: Update.value.Patient?.ID,
              counts: Update.value.Studies,
            };

            dispatch(studyCountModel.actions.setOne(studyCount));

            break;
          }

          case 'SharedWithMeCount': {
            dispatch(patientModel.actions.setSharedWithMeCount(Update.value));
            break;
          }

          case 'SharedByMeCount': {
            dispatch(patientModel.actions.setSharedByMeCount(Update.value));
            break;
          }

          case 'TotalOrganizationPatientCount': {
            dispatch(
              patientModel.actions.setTotalOrganizationPatientCount(
                Update.value,
              ),
            );
          }
        }
      }
    } catch (error) {
      debug('Error in usePatientsListStream', error);

      const connectError = ConnectError.from(error);
      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closePatientListStream(false);
        openPatientListStream({
          searchString: searchValue,
          doctorFilters: doctorFilter,
          id: '',
          studyTypes: studyTypesFilter,
          ageGroup: ageGroupFilter,
          lastModifiedFrom: lastModifiedFromUrl,
          lastModifiedTo: lastModifiedToUrl,
        });
      }
    }
  };

  const closePatientListStream = (resetStore = true) => {
    if (abortController) {
      abortController.abort();
    }

    if (resetStore) {
      dispatch(patientModel.actions.removeAll());
    }
  };

  return { openPatientListStream, closePatientListStream };
};
